import React, { useState, useMemo, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import qapi from "views_shared/app/javascript/api";

const MediaOutletContext = React.createContext(null);

const CONTACT_OWNER_TYPE = "Id::MediaOutlet";

// Media Outlet's state provider — the shared provider/context pattern (see profiles/README.md).
// Owns the outlet record and its contact infos, plus the edit-surface plumbing (open modal, saving
// flag, recoverable save error) the admin curation modals read. The contact infos come from the raw
// contact_infos read rather than the outlet row's contact_details aggregate: the aggregate leaves out
// email and phone, which are exactly the newsroom details the Contact card and its editor exist for.
function MediaOutletProvider(props) {
  const { children } = props;

  // eslint-disable-next-line react/destructuring-assignment
  const { viewerContext = {} } = props.value;

  // alphabetized
  const [asyncError, setAsyncError] = useState(null);
  const [contactInfos, setContactInfos] = useState([]);
  const [generalError, setGeneralError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [mediaOutlet, setMediaOutlet] = useState(null);
  const [mediaOutletId, setMediaOutletId] = useState(null);
  const [openEditModal, setOpenEditModal] = useState(null);

  const loadOutlet = useCallback(
    async ({ silent = false } = {}) => {
      if (!mediaOutletId) return;
      try {
        if (!silent) setIsLoading(true);

        const outletPromise = qapi.mediaOutlets.get(mediaOutletId).then((response) => {
          // Gate on the returned row, not the estimated count (count=estimated can misreport).
          const rows = Array.isArray(response.data) ? response.data : [];
          setMediaOutlet(rows.length ? rows[0] : null);
        });

        // Contact details are page enrichment, not the page itself: a failed read leaves the Contact
        // card empty rather than tearing the outlet down behind the error boundary. The read resolves
        // a non-2xx as `{ error }` rather than rejecting, so that shape is degraded here deliberately
        // rather than by falling through the array check; the .catch covers an outright rejection.
        const contactsPromise = qapi.contactInfos
          .getAllByContactOwner(mediaOutletId, CONTACT_OWNER_TYPE)
          .then((response) => {
            if (response.error || !Array.isArray(response.data)) {
              setContactInfos([]);
              return;
            }
            setContactInfos(response.data);
          })
          .catch(() => setContactInfos([]));

        await Promise.all([outletPromise, contactsPromise]);
      } catch (error) {
        setAsyncError(error);
      } finally {
        if (!silent) setIsLoading(false);
      }
    },
    [mediaOutletId],
  );

  useEffect(() => {
    if (mediaOutletId) loadOutlet();
  }, [mediaOutletId, loadOutlet]);

  const silentReload = useCallback(() => loadOutlet({ silent: true }), [loadOutlet]);

  const providerValue = useMemo(
    () => ({
      // alphabetized
      asyncError,
      contactInfos,
      generalError,
      isLoading,
      isUpdating,
      mediaOutlet,
      mediaOutletId,
      openEditModal,
      setAsyncError,
      setGeneralError,
      setIsUpdating,
      setMediaOutletId,
      setOpenEditModal,
      silentReload,
      viewerContext,
    }),
    [
      asyncError,
      contactInfos,
      generalError,
      isLoading,
      isUpdating,
      mediaOutlet,
      mediaOutletId,
      openEditModal,
      silentReload,
      viewerContext,
    ],
  );

  return <MediaOutletContext.Provider value={providerValue}>{children}</MediaOutletContext.Provider>;
}

export { MediaOutletContext, MediaOutletProvider, CONTACT_OWNER_TYPE };

MediaOutletProvider.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
};
